import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Avatar,
  Stack,
  Divider,
  TextField,
  MenuItem,
  Pagination,
  InputAdornment,
  CircularProgress,
  Tooltip,
  Alert
} from '@mui/material';
import {
  History as HistoryIcon,
  Visibility as ViewIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  AccountBalanceWallet as WalletIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Store as StoreIcon,
  ZoomIn as ZoomIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { fetchAllTransactions, fetchTransactionById } from '../transactionApi';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../BranchContext';

const TransactionHistory = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { selectedBranch } = useBranch();
  
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filterStatus, setFilterStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadTransactions();
  }, [selectedBranch, page, filterStatus]);

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const branchId = user.Permission === true ? null : (selectedBranch?.Id || user.BranchId);
      const data = await fetchAllTransactions(branchId, filterStatus || null, page, 50);
      setTransactions(data.transactions || []);
      setTotalPages(data.pagination?.totalPages || 1);
      setTotalCount(data.pagination?.total || 0);
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (transaction) => {
    try {
      const details = await fetchTransactionById(transaction.Id);
      setSelectedTransaction(details);
      setViewDialogOpen(true);
    } catch (error) {
      console.error('Error loading details:', error);
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <ApprovedIcon />;
      case 'rejected': return <RejectedIcon />;
      default: return null;
    }
  };

  const getCurrencyDisplay = (transaction) => {
    if (transaction.FormattedAmount) {
      return transaction.FormattedAmount;
    }
    const symbol = transaction.CurrencySymbol || '';
    const amount = transaction.Amount || 0;
    return symbol + amount;
  };

  const filteredTransactions = transactions.filter(txn =>
    txn.TransactionId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    txn.OrderId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    txn.User?.FullName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const approvedCount = transactions.filter(t => t.Status === 'approved').length;
  const rejectedCount = transactions.filter(t => t.Status === 'rejected').length;

  return (
    <Box sx={{ p: 3 }}>
      <Card elevation={3}>
        <CardHeader
          title={
            <Stack direction="row" alignItems="center" spacing={2}>
              <HistoryIcon color="primary" fontSize="large" />
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  {t('TransactionHistory') || 'Transaction History'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('ViewCompletedTransactions') || 'View all completed payment transactions'}
                </Typography>
              </Box>
            </Stack>
          }
          action={
            <Chip 
              label={`${totalCount} ${t('Total')}`}
              color="primary"
              sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}
            />
          }
          sx={{ 
            bgcolor: 'background.paper',
            borderBottom: 2,
            borderColor: 'primary.main',
            pb: 2
          }}
        />
        
        {/* Filters */}
        <CardContent sx={{ pb: 0 }}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder={t('SearchTransactions') || 'Search by transaction ID, order ID, or customer...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ bgcolor: 'background.paper' }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                size="small"
                label={t('FilterByStatus') || 'Filter by Status'}
                value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FilterIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ bgcolor: 'background.paper' }}
              >
                <MenuItem value="">{t('AllStatuses') || 'All Statuses'}</MenuItem>
                <MenuItem value="approved">{t('Approved')}</MenuItem>
                <MenuItem value="rejected">{t('Rejected')}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <Stack direction="row" spacing={1} height="100%" alignItems="center">
                <Chip
                  icon={<ApprovedIcon />}
                  label={`${approvedCount} ${t('Approved')}`}
                  color="success"
                  variant="outlined"
                  sx={{ flex: 1, fontWeight: 600 }}
                />
                <Chip
                  icon={<RejectedIcon />}
                  label={`${rejectedCount} ${t('Rejected')}`}
                  color="error"
                  variant="outlined"
                  sx={{ flex: 1, fontWeight: 600 }}
                />
              </Stack>
            </Grid>
          </Grid>
        </CardContent>

        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.light', color: 'white' }}>
                      {t('TransactionID')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.light', color: 'white' }}>
                      {t('OrderID')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.light', color: 'white' }}>
                      {t('Customer')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.light', color: 'white' }}>
                      {t('Amount')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.light', color: 'white' }}>
                      {t('Wallet')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.light', color: 'white' }}>
                      {t('Status')}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.light', color: 'white' }}>
                      {t('ProcessedBy') || 'Processed By'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.light', color: 'white' }}>
                      {t('Date')}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'primary.light', color: 'white' }}>
                      {t('Actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTransactions.map((txn) => (
                    <TableRow key={txn.Id} hover sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                      <TableCell>
                        <Tooltip title="Click to view details">
                          <Typography 
                            variant="body2" 
                            fontWeight="bold" 
                            color="primary.main"
                            sx={{ cursor: 'pointer' }}
                            onClick={() => handleViewDetails(txn)}
                          >
                            {txn.TransactionId}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip label={txn.OrderId} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
                            {txn.User?.FullName?.charAt(0) || 'U'}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="500">
                              {txn.User?.FullName || '-'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {txn.User?.Phone || ''}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getCurrencyDisplay(txn)}
                          size="small"
                          sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}
                          color={txn.Status === 'approved' ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {txn.Wallet?.ImageUrl && (
                            <Avatar src={txn.Wallet.ImageUrl} sx={{ width: 28, height: 28 }} />
                          )}
                          <Box>
                            <Typography variant="body2" fontWeight="500">
                              {txn.WalletName}
                            </Typography>
                            <Chip 
                              label={txn.WalletNumber} 
                              size="small" 
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(txn.Status)}
                          label={t(txn.Status === 'approved' ? 'Approved' : 'Rejected')}
                          color={getStatusColor(txn.Status)}
                          size="small"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="500">
                          {txn.Approval?.ApprovedBy || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="caption" display="block" fontWeight="500">
                            {new Date(txn.CreatedAt).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(txn.UpdatedAt).toLocaleTimeString()}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Details">
                          <IconButton 
                            color="info" 
                            onClick={() => handleViewDetails(txn)} 
                            size="small"
                            sx={{ '&:hover': { bgcolor: 'info.light' } }}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredTransactions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Box sx={{ py: 6 }}>
                          <HistoryIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                          <Typography variant="h6" color="text.secondary">
                            {t('NoTransactionsFound') || 'No transactions found'}
                          </Typography>
                          <Typography variant="body2" color="text.disabled">
                            {searchQuery ? t('TryDifferentSearch') : t('NoHistoryYet')}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3, gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t('Page')} {page} {t('of')} {totalPages}
              </Typography>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
                size="large"
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* View Details Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ bgcolor: getStatusColor(selectedTransaction?.Status) + '.light', color: 'white', borderRadius: '12px 12px 0 0' }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <HistoryIcon />
            <Typography variant="h6">{t('TransactionDetails')}</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedTransaction && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Alert 
                  severity={selectedTransaction.Status === 'approved' ? 'success' : 'error'}
                  icon={getStatusIcon(selectedTransaction.Status)}
                >
                  <Typography variant="body2">
                    <strong>{selectedTransaction.TransactionId}</strong> - {selectedTransaction.Title}
                  </Typography>
                </Alert>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack spacing={2.5}>
                  <Paper elevation={1} sx={{ p: 2, borderLeft: 3, borderColor: 'primary.main' }}>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      {t('OrderID')}
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">#{selectedTransaction.OrderId}</Typography>
                  </Paper>

                  <Paper elevation={1} sx={{ p: 2, borderLeft: 3, borderColor: getStatusColor(selectedTransaction.Status) + '.main' }}>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      {t('Amount')}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="h5" color={getStatusColor(selectedTransaction.Status) + '.main'} fontWeight="bold">
                        {getCurrencyDisplay(selectedTransaction)}
                      </Typography>
                      {selectedTransaction.CurrencyName && (
                        <Chip 
                          label={selectedTransaction.CurrencyName} 
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  </Paper>

                  <Paper elevation={1} sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Stack spacing={1.5}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {selectedTransaction.Wallet?.ImageUrl ? (
                          <Avatar src={selectedTransaction.Wallet.ImageUrl} sx={{ width: 40, height: 40 }} />
                        ) : (
                          <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}>
                            <WalletIcon />
                          </Avatar>
                        )}
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t('Wallet')}
                          </Typography>
                          <Typography variant="body1" fontWeight="600">
                            {selectedTransaction.WalletName}
                          </Typography>
                        </Box>
                      </Stack>
                      <Divider />
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">
                          {t('WalletNumber')}
                        </Typography>
                        <Chip 
                          label={selectedTransaction.WalletNumber} 
                          size="small" 
                          color="primary"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </Stack>
                    </Stack>
                  </Paper>

                  <Paper elevation={1} sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      {t('Customer')}
                    </Typography>
                    <Stack spacing={1} mt={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PersonIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {selectedTransaction.User?.FullName || '-'}
                        </Typography>
                      </Stack>
                      {selectedTransaction.User?.Phone && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <PhoneIcon fontSize="small" color="action" />
                          <Typography variant="body2">
                            {selectedTransaction.User.Phone}
                          </Typography>
                        </Stack>
                      )}
                      {selectedTransaction.User?.Email && (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <EmailIcon fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">
                            {selectedTransaction.User.Email}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  </Paper>

                  {selectedTransaction.Branch && (
                    <Paper elevation={1} sx={{ p: 2 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <StoreIcon color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {t('Branch')}
                          </Typography>
                          <Typography variant="body2" fontWeight="500">
                            {selectedTransaction.Branch.Name}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  )}

                  {selectedTransaction.Approval && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Paper elevation={1} sx={{ p: 2, bgcolor: 'background.default' }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          {t('ApprovalDetails') || 'Approval Details'}
                        </Typography>
                        <Stack spacing={1.5} mt={1}>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="caption" color="text.secondary">
                              {t('ProcessedBy')}
                            </Typography>
                            <Typography variant="body2" fontWeight="600">
                              {selectedTransaction.Approval.ApprovedBy}
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="caption" color="text.secondary">
                              {t('ProcessedAt') || 'Processed At'}
                            </Typography>
                            <Typography variant="caption">
                              {new Date(selectedTransaction.Approval.ApprovedAt).toLocaleString()}
                            </Typography>
                          </Stack>
                          {selectedTransaction.Approval.RejectionReason && (
                            <Alert severity="error" sx={{ mt: 1 }}>
                              <Typography variant="caption" fontWeight="bold">
                                {t('RejectionReason')}:
                              </Typography>
                              <Typography variant="body2">
                                {selectedTransaction.Approval.RejectionReason}
                              </Typography>
                            </Alert>
                          )}
                          {selectedTransaction.Approval.Notes && (
                            <Box sx={{ p: 1.5, bgcolor: 'info.lighter', borderRadius: 1, mt: 1 }}>
                              <Typography variant="caption" color="text.secondary" fontWeight="bold">
                                {t('Notes')}:
                              </Typography>
                              <Typography variant="body2" color="text.primary">
                                {selectedTransaction.Approval.Notes}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      </Paper>
                    </>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                  <Stack spacing={1} height="100%">
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle2" color="text.secondary">
                        {t('PaymentProof')}
                      </Typography>
                      {selectedTransaction.TransactionImageUrl && (
                        <Tooltip title="View full size">
                          <IconButton 
                            size="small" 
                            onClick={() => setImagePreviewOpen(true)}
                            color="primary"
                          >
                            <ZoomIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                    <Divider />
                    {selectedTransaction.TransactionImageUrl ? (
                      <Box
                        component="img"
                        src={selectedTransaction.TransactionImageUrl}
                        alt="Payment Proof"
                        onClick={() => setImagePreviewOpen(true)}
                        sx={{
                          width: '100%',
                          height: 'auto',
                          maxHeight: 450,
                          objectFit: 'contain',
                          border: '2px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          bgcolor: '#f9f9f9',
                          cursor: 'pointer',
                          transition: 'transform 0.2s',
                          '&:hover': {
                            transform: 'scale(1.02)',
                            boxShadow: 3
                          }
                        }}
                      />
                    ) : (
                      <Box 
                        sx={{ 
                          p: 6, 
                          textAlign: 'center', 
                          bgcolor: '#f5f5f5', 
                          borderRadius: 2,
                          border: '2px dashed',
                          borderColor: 'divider'
                        }}
                      >
                        <WalletIcon sx={{ fontSize: 80, color: '#ccc', mb: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                          {t('NoImageUploaded') || 'No payment proof image'}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: 'background.default' }}>
          <Button onClick={() => setViewDialogOpen(false)} color="inherit">
            {t('Close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={imagePreviewOpen}
        onClose={() => setImagePreviewOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'black' } }}
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          {selectedTransaction?.TransactionImageUrl && (
            <Box
              component="img"
              src={selectedTransaction.TransactionImageUrl}
              alt="Payment Proof Full Size"
              sx={{
                width: '100%',
                height: 'auto',
                maxHeight: '90vh',
                objectFit: 'contain'
              }}
            />
          )}
          <IconButton
            onClick={() => setImagePreviewOpen(false)}
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              bgcolor: 'rgba(255,255,255,0.9)',
              '&:hover': { bgcolor: 'white' }
            }}
          >
            <RejectedIcon />
          </IconButton>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default TransactionHistory;
